import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {TemplateNode} from "#template/template-node/template-node"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {pointGeometryAttributeSetLike} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {PointGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {GenerateGeometry} from "#template/runtime-graph/nodes/generate-geometry"
import type {ValueType} from "#template/geometry-processing/geometry-graph"
import {elementCountAndTypeFromValueType} from "#template/geometry-processing/geometry-graph"
import type {ReifiedGeometryData, ReifiedGeometryAttributeData, ReifiedGeometryAttributeSetData} from "#template/interfaces/geometry-data"
import type {ReifiedPointData, CompletePointData} from "#template/interfaces/point-data"
import type {ExprsTreeTokenSet} from "#template/nodes/geometry-graph/common/geometry-graph-evaluation"
import {
    reifiedGeometryDataToReifiedGeometryAttributeSetData,
    setupGeometryGenerationData,
    isReifiedGeometryAttributeData,
    isReifiedGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-evaluation"
import type {PointGeometryAttributeSetData, PointGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import {isGeometryAttributeData, isGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {visitNone} from "#template/template-node/visit-helpers"

function reifiedGeometryDataToReifiedPointData<T extends ExprsTreeTokenSet>(reifiedGeometryData: ReifiedGeometryData, exprsTreeTokenSet: T): ReifiedPointData {
    const reifiedAttributeSet = reifiedGeometryDataToReifiedGeometryAttributeSetData(reifiedGeometryData, exprsTreeTokenSet, 0)

    const assertSinglePointOnly = false
    const parseData = <T extends Float32Array | Uint32Array>(data: T, valueType: ValueType): T => {
        if (data instanceof Float32Array || data instanceof Uint32Array) {
            const {count} = elementCountAndTypeFromValueType(valueType)
            if (assertSinglePointOnly && data.length / count !== 1) throw new Error(`Expected single point data, but got ${data.length / count} points`)
            return data as T
        }
        throw new Error(`Invalid data`)
    }

    const traverse = <T extends ReifiedGeometryAttributeData | ReifiedGeometryAttributeSetData>(data: T) => {
        const pointData = (() => {
            if (isReifiedGeometryAttributeData(data)) {
                return {
                    valueType: data.valueType,
                    data: parseData(data.data, data.valueType),
                }
            } else if (isReifiedGeometryAttributeSetData(data)) {
                const set: ReifiedPointData["attributeSet"] = {}
                for (const [key, value] of Object.entries(data)) {
                    set[key] = traverse(value)
                }
                return set
            }

            throw new Error(`Invalid data type`)
        })()
        return pointData as T extends ReifiedGeometryAttributeData ? Omit<T, "indexingDim"> : ReifiedPointData["attributeSet"]
    }

    return {
        contentHash: reifiedGeometryData.contentHash,
        attributeSet: traverse(reifiedAttributeSet),
    }
}

function isValidPointGeometryAttributeSetData(attributeSetData: PointGeometryAttributeSetData | null) {
    if (attributeSetData === null) return false

    let foundAttributeData = false
    const traverse = <T extends PointGeometryAttributeSetData | PointGeometryAttributeData>(data: T) => {
        if (isGeometryAttributeData(data)) {
            foundAttributeData = true
            return
        } else if (isGeometryAttributeSetData(data)) {
            for (const value of Object.values(data.data)) traverse(value)
        } else throw new Error(`Invalid data type, expected PointGeometryAttributeSetData or PointGeometryAttributeData, got ${data}`)
    }
    traverse(attributeSetData)
    return foundAttributeData
}

export type GeometryGraphPointParameters = {
    name: string
    pointAttributeSet: PointGeometryAttributeSetLikeNode | null
}

const definition: TemplateNodeDefinition<GeometryGraphPointParameters> = {
    nodeClass: "GeometryGraphPoint",
    validation: {
        parameters: z.object({
            name: z.string(),
            pointAttributeSet: z.nullable(pointGeometryAttributeSetLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class GeometryGraphPoint extends TemplateNode<GeometryGraphPointParameters> implements EvaluableTemplateNode<CompletePointData | null> {
    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<GeometryGraphPointParameters> {
        if (this.parameters.pointAttributeSet === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<GeometryGraphPointParameters> {
        const {pointAttributeSet} = this.parameters
        if (pointAttributeSet === null) return skipped

        const {evaluator} = context
        const {templateScope, templateContext} = evaluator
        const {sceneManager} = templateContext

        const scope = evaluator.getScope(this)

        const deferredPointAttributeSetData = scope.waitForFinishedModifyingScene(evaluator.evaluate(pointAttributeSet), sceneManager)

        const [pointAttributeSetDataValid, pointAttributeSetDataInvalid] = scope.branch(
            scope.pureLambda(
                deferredPointAttributeSetData,
                (attributeSetData) => (isValidPointGeometryAttributeSetData(attributeSetData) ? attributeSetData : null),
                "pointAttributeSetDataValid",
            ),
        )

        const geometryGenerationData = scope.pureLambda(
            pointAttributeSetDataValid,
            (pointAttributeSetData) => setupGeometryGenerationData(pointAttributeSetData),
            "geometryGenerationData",
        )

        const geometryGenerationInputData = scope.get(geometryGenerationData, "input")

        const {completeGeometryData} = scope.node(GenerateGeometry, {
            sceneManager,
            inputGeometryData: geometryGenerationInputData,
        })

        const abstractGeometryData = scope.get(completeGeometryData, "abstract")
        const reifiedGeometryData = scope.get(completeGeometryData, "reified")
        const displayExprsTreeTokenSet = scope.get(scope.get(geometryGenerationData, "context"), "displayExprsTreeTokenSet")

        const reifiedPointData = scope.pureLambda(
            scope.tuple(reifiedGeometryData, displayExprsTreeTokenSet),
            ([reifiedGeometryData, exprsTreeTokenSet]) => reifiedGeometryDataToReifiedPointData(reifiedGeometryData, exprsTreeTokenSet),
            "reifiedPointData",
        )

        const pointData = scope.struct<CompletePointData>("CompletePointData", {
            abstract: abstractGeometryData,
            reified: reifiedPointData,
        })

        templateScope.alias(scope.phi(pointData, pointAttributeSetDataInvalid), `pointData-${evaluator.getLocalId(this)}`)

        return visitNone(this.parameters)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<CompletePointData | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<CompletePointData>(`pointData-${evaluator.getLocalId(this)}`))
    }
}
